import React, { useState, useRef, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Sparkles, X, Plus, ChevronDown, ChevronRight, Users } from "lucide-react";
import { cn } from "@/lib/utils";

interface InterestsPickerProps {
  selected: string[];
  onChange: (interests: string[]) => void;
  userId?: string;
  openToNetworking?: boolean;
  onNetworkingChange?: (value: boolean) => void;
}

interface PopularInterest {
  id: string;
  name: string;
  category: string | null;
  usage_count: number;
}

export const InterestsPicker: React.FC<InterestsPickerProps> = ({ 
  selected, 
  onChange,
  userId,
  openToNetworking = false,
  onNetworkingChange,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<string[]>(["cause"]);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  // Fetch popular interests for autocomplete and suggestions
  const { data: popularInterests } = useQuery({
    queryKey: ["popular-interests"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("popular_interests")
        .select("*")
        .order("usage_count", { ascending: false });
      
      if (error) throw error;
      return data as PopularInterest[];
    },
  });

  // Fetch AI suggestions based on user activity
  const { data: suggestions } = useQuery({
    queryKey: ["interest-suggestions", userId],
    queryFn: async () => {
      if (!userId) return [];
      
      // Get tags from saved resources
      const { data: savedItems } = await supabase
        .from("user_saved_resources")
        .select("resource_id")
        .eq("user_id", userId)
        .limit(20);
      
      if (!savedItems?.length) return [];

      const resourceIds = savedItems.map(s => s.resource_id);
      const { data: items } = await supabase
        .from("library_items")
        .select("tags, cause")
        .in("id", resourceIds);
      
      // Get tags from joined challenges
      const { data: teamMemberships } = await supabase
        .from("challenge_team_members")
        .select("challenges(tags)")
        .eq("user_id", userId);
      
      // Collect all tags
      const allTags: string[] = [];
      items?.forEach(item => {
        if (item.tags) allTags.push(...item.tags);
        if (item.cause) allTags.push(item.cause);
      });
      teamMemberships?.forEach(tm => {
        const challenge = tm.challenges as any;
        if (challenge?.tags) allTags.push(...challenge.tags);
      });
      
      // Count frequency and get top 5
      const tagCounts: Record<string, number> = {};
      allTags.forEach(tag => {
        const normalized = tag.toLowerCase().trim();
        tagCounts[normalized] = (tagCounts[normalized] || 0) + 1;
      });
      
      return Object.entries(tagCounts)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([tag]) => tag.charAt(0).toUpperCase() + tag.slice(1));
    },
    enabled: !!userId,
  });

  // Filter popular interests based on input
  const filteredSuggestions = inputValue.length >= 2 
    ? popularInterests?.filter(pi => 
        pi.name.toLowerCase().includes(inputValue.toLowerCase()) &&
        !selected.some(s => s.toLowerCase() === pi.name.toLowerCase())
      ).slice(0, 6)
    : [];

  // Group popular interests by category
  const interestsByCategory = popularInterests?.reduce((acc, interest) => {
    const category = interest.category || "other";
    if (!acc[category]) acc[category] = [];
    acc[category].push(interest);
    return acc;
  }, {} as Record<string, PopularInterest[]>) || {};

  const categoryLabels: Record<string, string> = {
    cause: "Causes & Works of Mercy",
    tech: "Technology",
    hobby: "Hobbies & Lifestyle",
    faith: "Faith & Spirituality",
    other: "Other",
  };

  const addInterest = (interest: string) => {
    const normalized = interest.trim();
    if (normalized && !selected.some(s => s.toLowerCase() === normalized.toLowerCase())) {
      onChange([...selected, normalized]);
    }
    setInputValue("");
    setShowSuggestions(false);
  };

  const removeInterest = (interest: string) => {
    onChange(selected.filter(s => s !== interest));
  };

  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => 
      prev.includes(category) 
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionsRef.current && 
        !suggestionsRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (inputValue.trim()) {
        addInterest(inputValue);
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* Current interests */}
      <div className="space-y-3">
        <Label className="text-base">Your Interests</Label>
        <p className="text-sm text-muted-foreground">
          What are you passionate about? Share causes you care about, hobbies you enjoy, 
          topics you love learning about, or anything else that makes you, you.
        </p>
        
        {/* Selected interests as tags */}
        <div className="flex flex-wrap gap-2 min-h-[44px] p-3 border rounded-lg bg-background">
          {selected.map(interest => (
            <Badge
              key={interest}
              variant="secondary"
              className="gap-1 px-3 py-1.5 text-sm"
            >
              {interest}
              <button
                type="button"
                onClick={() => removeInterest(interest)}
                className="ml-1 hover:text-destructive transition-colors"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
          
          {/* Input field */}
          <div className="relative flex-1 min-w-[200px]">
            <Input
              ref={inputRef}
              value={inputValue}
              onChange={(e) => {
                setInputValue(e.target.value);
                setShowSuggestions(true);
              }}
              onFocus={() => setShowSuggestions(true)}
              onKeyDown={handleKeyDown}
              placeholder={selected.length === 0 ? "Type to add interests..." : "Add more..."}
              className="border-0 shadow-none focus-visible:ring-0 px-0 h-8"
            />
            
            {/* Autocomplete dropdown */}
            {showSuggestions && inputValue.length >= 2 && (
              <div 
                ref={suggestionsRef}
                className="absolute top-full left-0 right-0 mt-1 bg-popover border rounded-md shadow-lg z-50 py-1"
              >
                {filteredSuggestions && filteredSuggestions.length > 0 ? (
                  <>
                    {filteredSuggestions.map(suggestion => (
                      <button
                        key={suggestion.id}
                        type="button"
                        onClick={() => addInterest(suggestion.name)}
                        className="w-full px-3 py-2 text-left text-sm hover:bg-accent flex items-center gap-2"
                      >
                        <span>{suggestion.name}</span>
                        {suggestion.category && (
                          <span className="text-xs text-muted-foreground">
                            {categoryLabels[suggestion.category] || suggestion.category}
                          </span>
                        )}
                      </button>
                    ))}
                    <div className="border-t my-1" />
                  </>
                ) : null}
                <button
                  type="button"
                  onClick={() => addInterest(inputValue)}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-accent flex items-center gap-2 text-primary"
                >
                  <Plus className="h-4 w-4" />
                  Add "{inputValue}" as new interest
                </button>
              </div>
            )}
          </div>
        </div>
        
        {selected.length > 0 && (
          <p className="text-xs text-muted-foreground">
            {selected.length} interest{selected.length !== 1 ? "s" : ""} added
          </p>
        )}
      </div>

      {/* AI Suggestions */}
      {suggestions && suggestions.length > 0 && (
        <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium">Suggested based on your activity</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {suggestions
              .filter(s => !selected.some(sel => sel.toLowerCase() === s.toLowerCase()))
              .map(suggestion => (
                <Badge
                  key={suggestion}
                  variant="outline"
                  className="cursor-pointer hover:bg-primary/10 border-primary/30 transition-colors"
                  onClick={() => addInterest(suggestion)}
                >
                  <Plus className="h-3 w-3 mr-1" />
                  {suggestion}
                </Badge>
              ))}
          </div>
        </div>
      )}

      {/* Popular interests by category */}
      <div className="space-y-2">
        <Label className="text-base">💡 Popular Interests</Label>
        <p className="text-sm text-muted-foreground mb-3">
          Click to add, or type your own above.
        </p>
        
        {Object.entries(interestsByCategory).map(([category, interests]) => (
          <Collapsible
            key={category}
            open={expandedCategories.includes(category)}
            onOpenChange={() => toggleCategory(category)}
          >
            <CollapsibleTrigger className="flex items-center gap-2 w-full py-2 text-sm font-medium hover:text-primary transition-colors">
              {expandedCategories.includes(category) ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
              {categoryLabels[category] || category}
              <span className="text-xs text-muted-foreground font-normal">
                ({interests.filter(i => selected.some(s => s.toLowerCase() === i.name.toLowerCase())).length} selected)
              </span>
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-2 pb-3">
              <div className="flex flex-wrap gap-2 pl-6">
                {interests.map(interest => {
                  const isSelected = selected.some(s => s.toLowerCase() === interest.name.toLowerCase());
                  return (
                    <Badge
                      key={interest.id}
                      variant={isSelected ? "default" : "outline"}
                      className={cn(
                        "cursor-pointer transition-all",
                        isSelected 
                          ? "bg-primary hover:bg-primary/90" 
                          : "hover:bg-accent"
                      )}
                      onClick={() => isSelected ? removeInterest(interest.name) : addInterest(interest.name)}
                    >
                      {interest.name}
                    </Badge>
                  );
                })}
              </div>
            </CollapsibleContent>
          </Collapsible>
        ))}
      </div>

      {/* Networking toggle */}
      {onNetworkingChange && (
        <div className="border rounded-lg p-4 bg-muted/30">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <Users className="h-5 w-5 text-primary mt-0.5" />
              <div>
                <Label htmlFor="networking-toggle" className="text-base font-medium cursor-pointer">
                  Open to Connecting
                </Label>
                <p className="text-sm text-muted-foreground mt-1">
                  Let others with shared interests reach out to you for collaboration, 
                  mentorship, or networking opportunities.
                </p>
              </div>
            </div>
            <Switch
              id="networking-toggle"
              checked={openToNetworking}
              onCheckedChange={onNetworkingChange}
            />
          </div>
        </div>
      )}
    </div>
  );
};
